import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AlertService } from '../../Dashboard/_services/alert.service';
import { RefDataCacheService } from '../_services/ref-data-cache.service';

// A 401 here almost always means the JWT expired mid-session (tokens last
// 1 day, but back-office logins routinely stay open longer than that) -
// every guarded endpoint then just returns 401 with no central handling,
// so screens quietly rendered as if there were zero data instead of
// telling the user to log back in. This is the one place every request
// passes through, so it's the one place that can catch that and redirect
// instead of leaving every component's empty-state UI to stand in for
// "your session expired".
export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const alertService = inject(AlertService);
  const refCache = inject(RefDataCacheService);

  // Anything that changes data empties the reference-list cache once it
  // succeeds. POSTs that only read (lists, filters, searches) are left out.
  const READ_POST = /(list|filter|paged|search|details|lookup|picker|inventory|report|\/get)\b/i;
  const changesData = req.method === 'PUT' || req.method === 'PATCH' || req.method === 'DELETE' || (req.method === 'POST' && !READ_POST.test(req.url));
  const clearOnSuccess = tap({ next: (event: any) => { if (changesData && event?.status && event.status < 400) refCache.clear(); } });

  // "Remember me" login stores the token in localStorage; a non-remembered
  // login stores it in sessionStorage instead (see UserService.setLoginStatus).
  const userInfo = localStorage.getItem('authTokenB') ?? sessionStorage.getItem('authTokenB');
  const backeEndUrl = environment.apiUrl;
  const url = req.url;

  const handleAuthError = (error: unknown) => {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      localStorage.removeItem('authTokenB');
      sessionStorage.removeItem('authTokenB');
      if (!router.url.startsWith('/login')) {
        alertService.alert('Your session has expired. Please sign in again.', 'error');
        router.navigate(['/login']);
      }
    }
    return throwError(() => error);
  };

  // Full URLs are other services (Loqate, ...). Their 401 is about their own
  // key or credit - not our login - so it must never sign the user out (an
  // out-of-credit Loqate account logged users out on "Find address"). Only a
  // full URL pointing at our own API is treated like our requests.
  if (url.startsWith('http')) {
    return url.startsWith(backeEndUrl) ? next(req).pipe(catchError(handleAuthError)) : next(req);
  }

  if (userInfo) {
    const token = JSON.parse(userInfo).access_token;

    const authReq = req.clone({
      url: backeEndUrl + url,
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next(authReq).pipe(clearOnSuccess, catchError(handleAuthError));
  } else {
    const authReq = req.clone({
      url: backeEndUrl + url,
    });
    return next(authReq).pipe(catchError(handleAuthError));
  }
};
